import RestApiCollection from '@/api/RestApiCollection'

// An edit route starts its entity request before the page chunk is loaded and the form is built: the
// form's own request only went out after the whole form (every eager tab) had been mounted, ~300 ms
// later on a restaurant (tasks#211). The form's first load of the same entity takes the request over;
// an unused one expires.
const prefetched = new Map()

export function prefetchEntity(api, id) {
    if (id === undefined || id === null || id === '') return
    const key = `${api}/${id}`
    if (prefetched.has(key)) return
    let request
    try {
        request = RestApiCollection.get(api).get(id)
    } catch (e) {
        return
    }
    // a failed request is dropped at once, so the form's own load asks again
    request.catch(() => {
        if (prefetched.get(key) === request) prefetched.delete(key)
    })
    prefetched.set(key, request)
    setTimeout(() => {
        if (prefetched.get(key) === request) prefetched.delete(key)
    }, 10000)
}

// on logout / a missing login: a response fetched for one user is never handed to the next one
export function clearPrefetched() {
    prefetched.clear()
}

export function takePrefetched(api, id) {
    const key = `${api}/${id}`
    const request = prefetched.get(key)
    prefetched.delete(key)
    return request
}

// a route's beforeEnter: start the request, then let axios send it (a few microtasks later) before
// the route's component is resolved and built
export function prefetchRoute(api, param) {
    return (to, from, next) => {
        prefetchEntity(api, to.params[param])
        setTimeout(next, 0)
    }
}

// Every edit page whose form has loaded once is remembered as route name -> (api, route param), also
// in this browser's storage, so the next visit of that route starts the entity request while the
// page chunk is still loading (the router's beforeEach), without a beforeEnter on each route.
const FORM_ROUTES_KEY = 'ordit.formRoutes'
let formRoutes = null

function knownFormRoutes() {
    if (formRoutes) return formRoutes
    try {
        formRoutes = JSON.parse(localStorage.getItem(FORM_ROUTES_KEY)) || {}
    } catch (e) {
        formRoutes = {}
    }
    return formRoutes
}

export function rememberFormRoute(route, api, id) {
    if (!route || !route.name || id === undefined || id === null) return
    const params = route.params || {}
    const matching = Object.keys(params).filter(
        (name) => String(params[name]) === String(id)
    )
    // two params with the same value (/companies/17/addresses/17): which one is the form's is unknown
    if (matching.length !== 1) return
    const param = matching[0]
    const routes = knownFormRoutes()
    const known = routes[route.name]
    if (known && known.api === api && known.param === param) return
    routes[route.name] = { api, param }
    try {
        localStorage.setItem(FORM_ROUTES_KEY, JSON.stringify(routes))
    } catch (e) {
        // the map stays in memory only
    }
}

export function prefetchFormRoute(to, from) {
    const known = to && to.name && knownFormRoutes()[to.name]
    if (!known) return
    const id = to.params[known.param]
    // a tab / query change on the same entity loads nothing
    if (
        from &&
        from.name === to.name &&
        String(from.params[known.param]) === String(id)
    )
        return
    prefetchEntity(known.api, id)
}
